import { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../auth/firebase';
import { useAuth } from '../../auth/useAuth';
import { isValidEmail } from '../../utils/phone';
import { apiCall } from '../../api/client';

export function LoginPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect already-authenticated users to dashboard
  if (!authLoading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);

      // Check if provider needs onboarding
      try {
        const data = await apiCall<{ provider: { headshotUrl?: string; phone?: string } }>('/practices/mine');
        if (!data.provider.headshotUrl || !data.provider.phone) {
          navigate('/onboarding?step=provider');
          return;
        }
      } catch {
        // Provider record may not exist yet (new signup) — send to onboarding
        navigate('/onboarding?step=provider');
        return;
      }

      navigate('/dashboard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="header">
        <img src="/img/logo.svg" alt="SimplyTelehealth" style={{ width: '80px', height: '80px', marginBottom: '0.5rem' }} />
        <h1 id="practiceName">SimplyTelehealth</h1>
        <p>Telehealth made simple</p>
      </div>

      <div className="card" id="loginCard">
        <h2 className="mb-2">Sign In</h2>
        {error && <div className="alert alert--error">{error}</div>}
        <div className="form-group">
          <label htmlFor="loginEmail">Email</label>
          <input
            type="email"
            id="loginEmail"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="loginPassword">Password</label>
          <input
            type="password"
            id="loginPassword"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
          />
        </div>
        <button
          className="btn btn--primary btn--full mt-1"
          id="loginBtn"
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? <><span style={{ visibility: 'hidden' }}>Sign In</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Sign In'}
        </button>
        <p className="text-center text-sm mt-2">
          Don't have an account? <Link to="/signup">Sign up</Link>
        </p>
      </div>
    </div>
  );
}
